import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getCurrentUserCompanyId } from "@/lib/userUtils";
import { toast } from "sonner";

interface CreateIngredientInput {
  name: string;
  category?: string;
  standard_unit: string;
  purchase_quantity?: number;
  purchase_unit?: string;
  cost_per_standard_unit?: number;
}

export const useCreateIngredient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateIngredientInput) => {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) {
        throw new Error("Company ID not found");
      }

      // Check if ingredient already exists
      const { data: existing } = await supabase
        .from("ingredients")
        .select("id, name")
        .eq("company_id", companyId)
        .ilike("name", input.name.trim())
        .maybeSingle();

      if (existing) {
        // Return existing ingredient instead of creating duplicate
        return existing;
      }

      if (!input.standard_unit?.trim()) {
        throw new Error("Standard unit is required when creating an ingredient");
      }

      const insertData: any = {
        company_id: companyId,
        name: input.name.trim(),
        category: input.category || "food cost",
        standard_unit: input.standard_unit.trim(),
        purchase_quantity: input.purchase_quantity ?? 1,
        purchase_unit: input.purchase_unit?.trim() || input.standard_unit.trim(),
        total_purchase_price: 0,
        purchase_date: new Date().toISOString().split('T')[0],
        cost_per_standard_unit: input.cost_per_standard_unit ?? 0,
      };

      // Only include purchase_quantity if explicitly provided
      if (input.purchase_quantity !== undefined) {
        insertData.purchase_quantity = input.purchase_quantity;
      }

      const { data, error } = await supabase
        .from("ingredients")
        .insert(insertData)
        .select("id, name, category, cost_per_standard_unit, standard_unit, purchase_quantity")
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      // Invalidate the ingredients query cache so all components refresh
      queryClient.invalidateQueries({ queryKey: ["ingredients"] });
      // Only show success toast if a new ingredient was created (not if it already existed)
      // We can check this by seeing if the data has the full structure
      if (data && "category" in data) {
        toast.success(`Ingredient "${data.name}" created successfully`);
      }
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to create ingredient");
    },
  });
};
